var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MeshInstance } from "./MeshInstance";
import { Renderable, RenderableUpdateTriggerState } from "../renderable/Renderable";
import { BusyIndicator, MulticastDelegate1, CameraLayers } from "flavr-common";
import { Material } from "three";
import { Box3, Vector3 } from "flavr-math";
import { proxyService } from "./ProxyService";
import { now } from "@messages/timing";
import { ReflectionProbeRenderable } from "../reflectionProbe/ReflectionProbeRenderable";
import { renderer } from "../RendererGlobals";
import { mainScene } from "../mainScene";
import { getRendererRootNode as getSharedRendererRootNode } from "../SceneRoots";
import { camera } from "../camera";
import { modelPool } from "@modules/model/ModelPool";
import { meshLoadingTracker } from "./MeshLoadingTracker";
import { onRoomBoundariesLoadedDelegate } from "@modules/bounds/MeshBoundingService";
import { preferencesService } from "@modules/preferences/PreferencesService";
import { roomTransitionLoadingState } from "../RoomTransitionLoadingState";
const tempSize = new Vector3();
const tmpBoundingBox = new Box3();
const WARMUP_IDLE_WINDOW_MS = 250;
const WARMUP_MAX_WAIT_MS = 2000;
const WARMUP_POLL_MS = 100;
export const onMeshLoadedDelegate = new MulticastDelegate1();
export class MeshRenderable extends Renderable {
    static notifyGraphicsSettingsChanged(preferenceKey) {
        if (!MeshRenderable.graphicsWarmupPreferenceKeys.has(preferenceKey))
            return;
        MeshRenderable.graphicsWarmupRevision += 1;
        if (MeshRenderable.activeRoomUid === undefined)
            return;
        MeshRenderable.queueWarmupIfReady("graphics-settings-changed");
    }
    static notifyRoomEntered(roomUid) {
        roomTransitionLoadingState.markRoomEntered(roomUid);
        MeshRenderable.activeRoomUid = roomUid;
        MeshRenderable.activeRoomEnteredAt = now();
        MeshRenderable.clearWarmupStartTimer();
        MeshRenderable.getOrCreateRoomWarmupState(roomUid);
        MeshRenderable.queueWarmupIfReady("room-entered");
    }
    static notifyEnvironmentFinalized(roomUid) {
        const state = MeshRenderable.getOrCreateRoomWarmupState(roomUid);
        state.environmentFinalizedAt = now();
        if (MeshRenderable.activeRoomUid !== roomUid)
            return;
        MeshRenderable.queueWarmupIfReady("environment-finalized");
    }
    static addPendingPrecompile(initializedTextures, roomUid) {
        var _a;
        if (initializedTextures <= 0)
            return;
        const resolvedRoomUid = roomUid !== null && roomUid !== void 0 ? roomUid : (_a = modelPool.getCurrentRoom()) === null || _a === void 0 ? void 0 : _a.getUID();
        if (resolvedRoomUid === undefined)
            return;
        const state = MeshRenderable.getOrCreateRoomWarmupState(resolvedRoomUid);
        state.lastMeshActivityAt = now();
        if (MeshRenderable.activeRoomUid === resolvedRoomUid) {
            MeshRenderable.queueWarmupIfReady("mesh-activity");
        }
    }
    static precompileMainSceneIncludingCulledAndHidden(startedAt, roomUid, warmupKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const previousState = [];
            let skippedRawShaderObjects = 0;
            let compileMode = "compile";
            let compileSucceeded = false;
            const warmupRoots = MeshRenderable.getWarmupRoots(roomUid);
            try {
                for (const root of warmupRoots) {
                    root.traverse((obj) => {
                        previousState.push({
                            obj,
                            visible: obj.visible,
                            frustumCulled: obj.frustumCulled
                        });
                        obj.visible = true;
                        obj.frustumCulled = false;
                        const mesh = obj;
                        if (mesh.material === undefined)
                            return;
                        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                        if (materials.some((material) => !!(material === null || material === void 0 ? void 0 : material.isRawShaderMaterial))) {
                            skippedRawShaderObjects += 1;
                            obj.visible = false;
                        }
                    });
                }
                camera.perspectiveCam.updateMatrixWorld(true);
                for (const root of warmupRoots) {
                    root.updateMatrixWorld(true);
                }
                const rendererWithAsync = renderer;
                if (typeof rendererWithAsync.compileAsync === "function") {
                    compileMode = "compileAsync";
                    for (const root of warmupRoots) {
                        yield rendererWithAsync.compileAsync(root, camera.perspectiveCam);
                    }
                }
                else {
                    for (const root of warmupRoots) {
                        renderer.compile(root, camera.perspectiveCam);
                    }
                }
                compileSucceeded = true;
            }
            finally {
                for (const state of previousState) {
                    state.obj.visible = state.visible;
                    state.obj.frustumCulled = state.frustumCulled;
                }
                const duration = now() - startedAt;
                const envKind = MeshRenderable.getCurrentEnvironmentKind();
                console.info(`[prewarm] done (room=${roomUid}, env=${envKind}, ${duration.toFixed(2)}ms, mode=${compileMode}, traversed=${previousState.length}, rawSkipped=${skippedRawShaderObjects})`);
                if (compileSucceeded) {
                    const state = MeshRenderable.getOrCreateRoomWarmupState(roomUid);
                    state.lastWarmupAt = now();
                    state.lastWarmupKey = warmupKey;
                }
                MeshRenderable.warmupInProgress = false;
                roomTransitionLoadingState.markPrewarmDone(roomUid);
                if (MeshRenderable.activeRoomUid === roomUid) {
                    MeshRenderable.queueWarmupIfReady("post-compile");
                }
            }
        });
    }
    static getCurrentWarmupKey(roomUid) {
        const environment = mainScene.environment;
        if (environment === undefined || environment === null)
            return `${roomUid}|none|gfx=${MeshRenderable.graphicsWarmupRevision}`;
        const texture = environment;
        const envKind = texture.isRenderTargetTexture ? "hdr" : "ldr";
        return `${roomUid}|${texture.uuid}|${envKind}|${texture.mapping}|${texture.colorSpace}|gfx=${MeshRenderable.graphicsWarmupRevision}`;
    }
    static getCurrentEnvironmentKind() {
        const environment = mainScene.environment;
        if (environment === undefined || environment === null)
            return "none";
        const texture = environment;
        return texture.isRenderTargetTexture ? "hdr" : "ldr";
    }
    static getOrCreateRoomWarmupState(roomUid) {
        let state = MeshRenderable.roomWarmupState.get(roomUid);
        if (state === undefined) {
            state = {
                lastMeshActivityAt: 0,
                boundariesLoadedAt: 0,
                environmentFinalizedAt: 0,
                lastWarmupAt: 0
            };
            MeshRenderable.roomWarmupState.set(roomUid, state);
        }
        return state;
    }
    static queueWarmupIfReady(reason) {
        const roomUid = MeshRenderable.activeRoomUid;
        if (roomUid === undefined)
            return;
        if (MeshRenderable.warmupInProgress)
            return;
        const state = MeshRenderable.getOrCreateRoomWarmupState(roomUid);
        const warmupKey = MeshRenderable.getCurrentWarmupKey(roomUid);
        const isAlreadyWarmed = state.lastWarmupKey === warmupKey && state.lastWarmupAt >= state.lastMeshActivityAt;
        if (state.boundariesLoadedAt <= 0)
            return;
        if (state.environmentFinalizedAt < MeshRenderable.activeRoomEnteredAt)
            return;
        if (isAlreadyWarmed) {
            if (reason === "post-compile")
                return;
            console.info(`[prewarm] skipped (already warmed, room=${roomUid})`);
            roomTransitionLoadingState.markPrewarmSkipped(roomUid);
            return;
        }
        if (MeshRenderable.warmupStartTimer !== undefined)
            return;
        MeshRenderable.scheduleWarmupStart(roomUid, now());
    }
    static notifyRoomBoundariesLoaded(roomUid) {
        const state = MeshRenderable.getOrCreateRoomWarmupState(roomUid);
        state.boundariesLoadedAt = now();
        if (MeshRenderable.activeRoomUid !== roomUid)
            return;
        MeshRenderable.queueWarmupIfReady("boundaries-loaded");
    }
    static scheduleWarmupStart(roomUid, queuedAt, idleSince) {
        const currentTime = now();
        const waitedMs = currentTime - queuedAt;
        const state = MeshRenderable.getOrCreateRoomWarmupState(roomUid);
        const hasPendingLoads = meshLoadingTracker.hasPendingLoads();
        const meshActivityAt = state.lastMeshActivityAt;
        const isMeshQuiet = meshActivityAt === 0 || currentTime - meshActivityAt >= WARMUP_IDLE_WINDOW_MS;
        // Start once loading is briefly idle and no recent mesh activity happened, or after a capped wait.
        if ((!hasPendingLoads && isMeshQuiet && idleSince !== undefined && currentTime - idleSince >= WARMUP_IDLE_WINDOW_MS) || waitedMs >= WARMUP_MAX_WAIT_MS) {
            MeshRenderable.warmupStartTimer = undefined;
            if (MeshRenderable.activeRoomUid !== roomUid)
                return;
            const startedAt = now();
            const warmupKey = MeshRenderable.getCurrentWarmupKey(roomUid);
            const envKind = MeshRenderable.getCurrentEnvironmentKind();
            MeshRenderable.warmupInProgress = true;
            console.info(`[prewarm] start (room=${roomUid}, env=${envKind}, delay=${(startedAt - queuedAt).toFixed(2)}ms)`);
            void MeshRenderable.precompileMainSceneIncludingCulledAndHidden(startedAt, roomUid, warmupKey);
            return;
        }
        const nextIdleSince = hasPendingLoads || !isMeshQuiet ? undefined : (idleSince !== null && idleSince !== void 0 ? idleSince : currentTime);
        MeshRenderable.warmupStartTimer = window.setTimeout(() => {
            MeshRenderable.scheduleWarmupStart(roomUid, queuedAt, nextIdleSince);
        }, WARMUP_POLL_MS);
    }
    static clearWarmupStartTimer() {
        if (MeshRenderable.warmupStartTimer === undefined)
            return;
        window.clearTimeout(MeshRenderable.warmupStartTimer);
        MeshRenderable.warmupStartTimer = undefined;
    }
    static getRendererRootNode() {
        var _a;
        return (_a = getSharedRendererRootNode()) !== null && _a !== void 0 ? _a : mainScene.children.find((child) => { var _a; return ((_a = child.userData) === null || _a === void 0 ? void 0 : _a.isRendererRoot) === true; });
    }
    static getWarmupRoots(roomUid) {
        if (roomUid === undefined) {
            return [mainScene];
        }
        const rendererRoot = MeshRenderable.getRendererRootNode();
        if (rendererRoot === undefined) {
            return [mainScene];
        }
        const roomRoot = rendererRoot.children.find((child) => { var _a; return ((_a = child.userData) === null || _a === void 0 ? void 0 : _a.roomUid) === roomUid; });
        if (roomRoot !== undefined) {
            return [roomRoot];
        }
        return [rendererRoot];
    }
    constructor(owner) {
        super(owner);
        this.type = MeshRenderable.typestatic;
        this.onLoadedDelegate = new MulticastDelegate1();
        this.onMeshStartLoading = () => {
            if (!this.meshInstance.getVisible())
                return;
            if (this.showBusyIndicator === undefined || this.showBusyIndicator)
                this.owner.updateFacet(new BusyIndicator(0, true), now());
        };
        this.onMeshLoaded = (group) => {
            var _a, _b;
            if (this.meshInstance.getVisible()) {
                if (this.showBusyIndicator === undefined || this.showBusyIndicator)
                    this.owner.updateFacet(new BusyIndicator(1, true), now());
            }
            this.updateTransform();
            this.setVisible(this.owner.visible);
            if (this.meshInstance.group && this.facet && this.facet.params.needsRescale) {
                this.meshInstance.group.updateMatrixWorld();
                tmpBoundingBox.setFromObject(this.meshInstance.group);
                tmpBoundingBox.getSize(tempSize);
                const maxDim = Math.max(tempSize.x, tempSize.y, tempSize.z);
                let scaleFactor = 1;
                if (maxDim !== 0) {
                    scaleFactor = 1 / maxDim;
                    this.meshInstance.group.scale.setScalar(scaleFactor);
                }
                tmpBoundingBox.getCenter(this.meshInstance.group.position);
                this.meshInstance.group.position.negate();
                this.meshInstance.group.position.multiplyScalar(scaleFactor);
                this.meshInstance.group.updateMatrix();
            }
            this.meshInstance.traverseMesh((mesh) => {
                var _a;
                mesh.layers.set(CameraLayers.Default);
                if (((_a = this.facet) === null || _a === void 0 ? void 0 : _a.params.isStatic) === true) {
                    mesh.layers.enable(CameraLayers.Static);
                    mesh.layers.enable(CameraLayers.Reflection);
                }
                else {
                    mesh.layers.enable(CameraLayers.Dynamic);
                }
            });
            this.cloneMaterials();
            this.updateMaterialColor();
            this.updateFrustumCulled();
            this.updateIsStatic();
            this.updateIgnoreCubeRendering();
            this.updateReflectionProbe(true);
            this.owner.addObjectToScene(group);
            const initializedTextures = this.prewarmTextures(group);
            const roomUid = (_b = (_a = modelPool.getModel(this.owner.modelUID)) === null || _a === void 0 ? void 0 : _a.getRoom()) === null || _b === void 0 ? void 0 : _b.getUID();
            MeshRenderable.addPendingPrecompile(initializedTextures, roomUid);
            if (this.meshInstance.getCastShadow()) {
                this.owner.renderer.updateShadowForModel(this.owner.modelUID);
            }
            this.onLoadedDelegate.invoke(this);
            onMeshLoadedDelegate.invoke(this);
            this.owner.triggerRenderableUpdate(this, RenderableUpdateTriggerState.Changed);
        };
        this.onUpdateReflectionProbe = (state, object) => {
            if (state === RenderableUpdateTriggerState.Changed) {
                if (object.envMap) {
                    this.meshInstance.traverseMaterial(Material, (mat) => {
                        if ("envMap" in mat && object.envMap) {
                            ;
                            mat.envMap = object.envMap;
                            mat.needsUpdate = true;
                        }
                    });
                }
            }
        };
        this.meshInstance = new MeshInstance(owner.renderer, this.onMeshLoaded, this.onMeshStartLoading, owner.modelUID);
        this.meshInstance.setVisible(false);
    }
    isCastShadow() {
        return this.meshInstance.getCastShadow();
    }
    prewarmTextures(group) {
        const initializedTextureUuids = new Set();
        group.traverse((obj) => {
            const mesh = obj;
            if (mesh.material === undefined)
                return;
            const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            for (const material of materials) {
                if (!material)
                    continue;
                for (const value of Object.values(material)) {
                    const texture = value;
                    if (!(texture === null || texture === void 0 ? void 0 : texture.isTexture))
                        continue;
                    if (initializedTextureUuids.has(texture.uuid))
                        continue;
                    initializedTextureUuids.add(texture.uuid);
                    renderer.initTexture(texture);
                }
            }
        });
        return initializedTextureUuids.size;
    }
    destroy() {
        if (this.showBusyIndicator === undefined || this.showBusyIndicator) {
            this.owner.removeFacet(BusyIndicator.typestatic, now());
        }
        super.destroy();
        this.meshInstance.destroy();
    }
    getGeometry() {
        return this.meshInstance.getGeometry();
    }
    setVisible(visible) {
        this.meshInstance.setVisible(visible);
        if (this.owner.matrixWorldValid && !this.meshInstance.isLoaded) {
            if (visible) {
                if (this.showBusyIndicator === undefined || this.showBusyIndicator)
                    this.owner.updateFacet(new BusyIndicator(0, true), now());
            }
            else {
                if (this.showBusyIndicator === undefined || this.showBusyIndicator)
                    this.owner.removeFacet(BusyIndicator.typestatic, now());
            }
        }
    }
    /**
     * Called when the facet is created(!) or when it changes.
     * @param facet
     */
    updateFacet(facet) {
        this.meshInstance.setCastShadow(!!facet.castShadow);
        this.meshInstance.setReceiveShadow(!!facet.receiveShadow);
        this.showBusyIndicator = facet.params.showBusyIndicator;
        const url = proxyService.proxiedUrlFor(facet.meshName);
        if (this.showBusyIndicator === false) {
            this.owner.removeFacet(BusyIndicator.typestatic, now());
        }
        this.meshInstance.load(url, false, facet.params.pieceName, facet.params.vertexScaleFactor, facet.params.applyOutline, facet.params.outlineColor, facet.params.cloneMaterials);
        this.meshInstance.updateSubMeshVisibilities(facet.subMeshVisibilities);
        this.facet = facet;
        this.cloneMaterials();
        this.updateMaterialColor();
        this.updateFrustumCulled();
        this.updateIsStatic();
        this.updateIgnoreCubeRendering();
        this.updateReflectionProbe();
    }
    updateTransform() {
        this.meshInstance.updateTransform(this.owner.matrixWorld);
    }
    updateMaterialColor() {
        if (this.facet === undefined)
            return;
        if (!this.meshInstance.isLoaded)
            return;
        if (this.facet.params.colors === undefined)
            return;
        for (const materialName in this.facet.params.colors) {
            const color = this.facet.params.colors[materialName];
            const mat = this.meshInstance.findMaterialByName(materialName);
            if (mat === undefined)
                continue;
            if ("color" in mat) {
                ;
                mat.color.setHex(color);
            }
        }
    }
    cloneMaterials() {
        if (this.facet === undefined)
            return;
        if (!this.meshInstance.isLoaded)
            return;
        if (this.facet.params.cloneMaterialsForMaterialName) {
            for (const materialName of this.facet.params.cloneMaterialsForMaterialName) {
                this.meshInstance.traverseMaterialByName(materialName, (mat, mesh, matIndex) => {
                    if (Array.isArray(mesh.material)) {
                        if (matIndex !== undefined) {
                            mesh.material[matIndex] = mat.clone();
                        }
                    }
                    else {
                        mesh.material = mat.clone();
                    }
                });
            }
        }
        if (this.facet.params.cloneMaterialsForSubObjectName) {
            for (const subName of this.facet.params.cloneMaterialsForSubObjectName) {
                this.meshInstance.traverseMeshByObjectName(subName, (mesh) => {
                    if (Array.isArray(mesh.material)) {
                        for (let i = 0, len = mesh.material.length; i < len; ++i) {
                            mesh.material[i] = mesh.material[i].clone();
                        }
                    }
                    else {
                        mesh.material = mesh.material.clone();
                    }
                });
            }
        }
    }
    updateReflectionProbe(force = false) {
        if (!this.facet)
            return;
        if (!this.meshInstance.group)
            return;
        const isChanged = force || this.reflectionProbeUid !== this.facet.params.reflectionProbeUid;
        if (!isChanged)
            return;
        if (this.facet.params.reflectionProbeUid !== undefined) {
            // ToDo: clone material if probe uid different
            this.owner.subscribeOtherRenderable(this.facet.params.reflectionProbeUid, ReflectionProbeRenderable, this.onUpdateReflectionProbe);
        }
        else if (this.reflectionProbeUid !== undefined) {
            this.owner.unsubscribeOtherRenderable(this.reflectionProbeUid, ReflectionProbeRenderable, this.onUpdateReflectionProbe);
        }
        this.reflectionProbeUid = this.facet.params.reflectionProbeUid;
    }
    updateIsStatic() {
        this.meshInstance.traverseMesh((mesh) => {
            mesh.layers.set(CameraLayers.Default);
            if (!this.facet)
                return;
            if (this.facet.params.isStatic) {
                mesh.layers.enable(CameraLayers.Static);
                mesh.layers.enable(CameraLayers.Reflection);
            }
            else {
                mesh.layers.enable(CameraLayers.Dynamic);
            }
        });
    }
    updateIgnoreCubeRendering() {
        this.meshInstance.traverseMesh((mesh) => {
            if (!this.facet)
                return;
            if (this.facet.params.ignoreCubeMapRendering) {
                mesh.layers.disable(CameraLayers.Reflection);
            }
        });
    }
    updateFrustumCulled() {
        if (!this.facet)
            return;
        if (!this.meshInstance.group)
            return;
        if (this.facet.params.frustumCulled === undefined)
            return;
        this.meshInstance.group.frustumCulled = this.facet.params.frustumCulled;
    }
}
MeshRenderable.typestatic = "Mesh";
MeshRenderable.warmupInProgress = false;
MeshRenderable.activeRoomEnteredAt = 0;
MeshRenderable.graphicsWarmupRevision = 0;
MeshRenderable.graphicsWarmupPreferenceKeys = new Set([
    "materialQuality",
    "useDynamicLights",
    "shadowMapType"
]);
MeshRenderable.roomWarmupState = new Map();
onRoomBoundariesLoadedDelegate.add((roomUid) => {
    MeshRenderable.notifyRoomBoundariesLoaded(roomUid);
});
preferencesService.onPreferenceChanged.add((key) => __awaiter(void 0, void 0, void 0, function* () {
    MeshRenderable.notifyGraphicsSettingsChanged(key);
    return true;
}));
